import { ResultFormat } from "components/Button/ResultFormatButton";
import { ButtonVariant, CoButton } from "components/CoButton";
import Popover from "components/Popover";
import SnapshotTooltip from "components/Popover/SnapshotTooltip";
import AskPaperTag from "components/Tag/tags/AskPaperTag";
import Tooltip from "components/Tooltip";
import { useBookmarks } from "features/Bookmarks";
import { usePaperSidebarDrawer } from "features/PaperSidebarDrawer";
import useUserSettings from "features/User/hooks/useUserSettings";
import { Paper, PaperDetails } from "helpers/api";
import { isPaperBookmarked, isUploadedPaperBookmarked } from "helpers/bookmark";
import { detailPageUrl, detailPagePath } from "helpers/pageUrl";
import { SearchResultTextType } from "helpers/search";
import { logAskPaperViaResultTag } from "helpers/services/mixpanel";
import {
  MixpanelActionSource,
  MixpanelCiteType,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useStudySnapshotToggle from "hooks/useStudySnapshotToggle";
import { useZoteroMetadata } from "hooks/useZoteroMetadata";
import React, { useCallback, useEffect, useState } from "react";
import { PaperQuote } from "store/slices/citedPapers";
import { CreditedFeature } from "store/slices/subscription";
import { YesNoType } from "types/YesNoType";
import SearchResultDefault from "./components/SearchResultDefault";
import SearchResultCompact from "./components/SearchResultCompact";
import SearchResultTableRow from "./components/SearchResultTableRow";
import useSubscription from "features/User/hooks/useSubscription";
import { FeatureFlag, useFeatureGate } from "features/Statsig";

type SearchResultProps = {
  citation?: number;
  extractedAnswer?: string;
  takeawayTranslation?: string;
  index: number;
  isLoadingExtractedAnswer?: boolean;
  isLoadingTakeawayTranslation?: boolean;
  shouldFetchTakeawayTranslation?: boolean;
  shouldBlockEnglishTakeaway?: boolean;
  isLoadingTitle?: boolean;
  isProcessingMetadata?: boolean;
  item: Paper;
  meter?: YesNoType;
  onClickAskPaper?: (
    item: Paper,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onClickItem?: (
    item: Paper,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onClickSave?: (item: Paper) => void;
  onClickCite?: (item: Paper) => void;
  onCitePaper?: (
    item: Paper,
    citeType: MixpanelCiteType,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onFullTextClick?: (
    paper: Paper | PaperDetails,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    index?: number,
    libraryId?: string
  ) => void;
  onOpenStudySnapshot?: (
    item: Paper,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onSharePaper?: (
    item: Paper,
    index: number,
    shareType: string,
    resultType?: SearchResultTextType
  ) => void;
  searchQuery?: { [key: string]: string | string[] | undefined };
  showPaperDoi?: boolean;
  hashPaperRecordId?: string;
  isUploading?: boolean;
  showSaveBtn?: boolean;
  showCiteBtn?: boolean;
  showShareBtn?: boolean;
  showManageUploadedPaperBtn?: boolean;
  mixpanelActionSource?: MixpanelActionSource;
  interactionId?: string;
  resultFormat?: ResultFormat;
  recentlyUpdated?: boolean;
  externalLink?: boolean;
  showCitationDashes?: boolean;
  openDetailsInNewTab?: boolean;
  recentlyAdded?: boolean;
  finalRanking?: boolean;
  directQuotes?: PaperQuote[];
  usePaperSidebar?: boolean;
};

/**
 * @component SearchResult
 * @description An individual search result
 */
const SearchResult = ({
  citation,
  extractedAnswer,
  takeawayTranslation,
  index,
  isLoadingExtractedAnswer,
  isLoadingTakeawayTranslation,
  shouldFetchTakeawayTranslation,
  shouldBlockEnglishTakeaway,
  isLoadingTitle,
  isProcessingMetadata,
  item,
  meter,
  onClickAskPaper,
  onClickItem,
  onClickSave,
  onClickCite,
  onCitePaper,
  onFullTextClick,
  onOpenStudySnapshot,
  onSharePaper,
  hashPaperRecordId,
  isUploading,
  searchQuery = {},
  showPaperDoi = false,
  showSaveBtn = false,
  showCiteBtn = false,
  showShareBtn = false,
  showManageUploadedPaperBtn = false,
  mixpanelActionSource,
  interactionId = "",
  resultFormat = ResultFormat.DEFAULT,
  recentlyUpdated = false,
  externalLink = false,
  showCitationDashes = false,
  openDetailsInNewTab = false,
  recentlyAdded = false,
  finalRanking = false,
  directQuotes,
  usePaperSidebar = false,
}: SearchResultProps) => {
  const [_isBookmarked, setIsBookmarked] = useState(false);
  const rawQueryText = searchQuery.q;
  const queryText = Array.isArray(rawQueryText)
    ? rawQueryText.join(" ")
    : rawQueryText || "";
  const { bookmarkItems } = useBookmarks();

  const { url_slug, paper_id, display_text, title, badges, is_retracted } =
    item;

  const isRetracted = is_retracted === true;
  const [searchLabels, researchHubUploadingStateLabels] = useLabels(
    "screens.search",
    "research-hub.uploading-state"
  );

  const { lang } = useUserSettings();
  const { openPaperSidebarDrawer } = usePaperSidebarDrawer();

  const [tooltipInstance, setTooltipInstance] = useState<any>(null);
  const initialText =
    extractedAnswer ||
    (shouldBlockEnglishTakeaway
      ? ""
      : shouldFetchTakeawayTranslation
        ? ""
        : display_text);
  const [text, setText] = useState<string>(initialText);
  const [textType, setTextType] = useState<SearchResultTextType | undefined>();
  const [snapshotEnabled, setSnapshotEnabled] = useState(
    !!extractedAnswer ||
      (!!display_text &&
        !shouldBlockEnglishTakeaway &&
        !shouldFetchTakeawayTranslation)
  );
  const [isManageDropdownOpen, setIsManageDropdownOpen] = useState(false);

  const handleManageDropdownOpenChange = (open: boolean) => {
    setIsManageDropdownOpen(open);
  };

  const isDataSourceSelectorEnabled = useFeatureGate(
    FeatureFlag.DATA_SOURCE_SELECTOR
  ).value;

  const { isFreePlan } = useSubscription();

  // Hide citations button for uploaded papers.
  // Note: Uploaded paper IDs start with "u:"
  const hideCitations = hashPaperRecordId?.includes("u:");

  const { isStudySnapshotOpened, toggleStudySnapshotOpened } =
    useStudySnapshotToggle(
      item.paper_id,
      onOpenStudySnapshot
        ? () => {
            onOpenStudySnapshot?.(item, index, textType);
          }
        : undefined,
      interactionId
    );

  const handleClickStudySnapshot = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleStudySnapshotOpened();
  };

  const handleClickUpgradePremium = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleHideTooltip();
  };

  useEffect(() => {
    /**
     * Wait for the extracted answer to be loaded before setting the text and text type.
     * If the extracted answer is not available, display the display text.
     * If the display text is not available, do not display any text and keep the snapshot disabled.
     */
    // Do not block if a translated takeaway is already available
    if (isLoadingExtractedAnswer && !extractedAnswer && !takeawayTranslation) {
      return;
    }

    // Only block on translation loading if there is an original display_text to translate
    // and a translation for this item is not yet available.
    if (
      shouldFetchTakeawayTranslation &&
      isLoadingTakeawayTranslation &&
      display_text &&
      !takeawayTranslation
    ) {
      return;
    }

    if (directQuotes && directQuotes.length > 0) {
      setSelectedQuoteIndex(undefined);
      setText(directQuotes[0].quote);
      setTextType(SearchResultTextType.DIRECT_QUOTE);
      setSnapshotEnabled(false);
    } else if (extractedAnswer) {
      setText(extractedAnswer);
      setTextType(SearchResultTextType.ANSWER);
      setSnapshotEnabled(true);
    } else if (takeawayTranslation) {
      // Use translated takeaway when available
      setText(takeawayTranslation);
      setTextType(SearchResultTextType.KEY_TAKEAWAY);
      setSnapshotEnabled(true);
    } else if (display_text && !shouldFetchTakeawayTranslation) {
      // Use original display_text only when no translation is expected (English language)
      setText(display_text);
      setTextType(SearchResultTextType.KEY_TAKEAWAY);
      setSnapshotEnabled(true);
    } else {
      setText(searchLabels["key-takeaway-not-available"]);
      setTextType(undefined);
    }
  }, [
    display_text,
    takeawayTranslation,
    extractedAnswer,
    isLoadingExtractedAnswer,
    isLoadingTakeawayTranslation,
    shouldFetchTakeawayTranslation,
    paper_id,
    searchQuery,
    url_slug,
    searchLabels,
    directQuotes,
  ]);

  useEffect(() => {
    if (hashPaperRecordId?.includes("u:")) {
      setIsBookmarked(
        isUploadedPaperBookmarked(hashPaperRecordId, bookmarkItems)
      );
    } else {
      setIsBookmarked(isPaperBookmarked(paper_id, bookmarkItems));
    }
  }, [setIsBookmarked, bookmarkItems, paper_id, hashPaperRecordId]);

  useZoteroMetadata(item.doi);

  const handleHideTooltip = useCallback(async () => {
    if (tooltipInstance != null) {
      tooltipInstance?.hide();
    }
  }, [tooltipInstance]);

  const handleResultClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target;
      const selectedText = window.getSelection()?.toString().trim() || "";
      if (
        (target instanceof HTMLElement && target.id === "tippy-backdrop") ||
        selectedText !== ""
      ) {
        e.preventDefault();
        return;
      }

      if (usePaperSidebar) {
        e.preventDefault();
        openPaperSidebarDrawer(item, interactionId);
        onClickItem?.(item, index, textType);
      }
    },
    [
      usePaperSidebar,
      openPaperSidebarDrawer,
      item,
      index,
      textType,
      onClickItem,
      interactionId,
    ]
  );

  const sequence = index === 0 ? "first" : "";

  let relativeLink: string = detailPagePath(url_slug, paper_id);
  let absoluteLink: string = detailPageUrl(url_slug, paper_id);
  const extraSearchParams: { [key: string]: string } = {};
  if (extractedAnswer) {
    extraSearchParams["extracted-answer"] = extractedAnswer;
  }

  if (Object.keys(extraSearchParams).length) {
    const searchParams = {
      ...extraSearchParams,
      ...searchQuery,
    };
    const searchParamsStr = new URLSearchParams(
      searchParams as Record<string, string>
    ).toString();
    relativeLink = `${detailPagePath(url_slug, paper_id)}?${searchParamsStr}`;
    absoluteLink = `${detailPageUrl(url_slug, paper_id)}?${searchParamsStr}`;
  }

  const extraTags: React.ReactNode[] = [];
  if (item.has_valid_chat_pdf) {
    extraTags.push(
      <AskPaperTag
        key="ask_paper"
        paper={item}
        onClickAskPaper={() => {
          logAskPaperViaResultTag(item.paper_id);
          onClickAskPaper?.(item, index, textType);
        }}
        isCompact={resultFormat !== ResultFormat.DEFAULT}
        interactionId={interactionId}
      />
    );
  }

  const [selectedQuoteIndex, setSelectedQuoteIndex] = useState<
    number | undefined
  >(undefined);

  const renderSnapshotButton = () => {
    if (!isFreePlan || (isStudySnapshotOpened && snapshotEnabled)) {
      const tooltipContent =
        isStudySnapshotOpened && snapshotEnabled
          ? searchLabels["close-snapshot"]
          : searchLabels["expand-study-snapshot"];

      return (
        <Tooltip content={tooltipContent} placement="bottom">
          <CoButton
            variant={ButtonVariant.Flat}
            size="sm"
            className="text-fg-muted hover:bg-bg-subtle"
            onClick={handleClickStudySnapshot}
            data-testid="snapshot-header-button"
            icon="icon-table-2"
            disabled={!snapshotEnabled}
            iconRight={
              isStudySnapshotOpened && snapshotEnabled
                ? "icon-chevron-up"
                : "icon-chevron-down"
            }
          />
        </Tooltip>
      );
    }

    return (
      <Popover
        interactive
        maxWidth={334}
        touch={false}
        tooltipContent={
          <SnapshotTooltip
            snapshotType={CreditedFeature.STUDY_SNAPSHOT}
            onClickUpgradePremium={handleClickUpgradePremium}
          />
        }
        disabled={!snapshotEnabled}
        onShown={(instance: any) => {
          setTooltipInstance(instance);
        }}
        onHidden={(instance: any) => {
          setTooltipInstance(instance);
        }}
      >
        <CoButton
          variant={ButtonVariant.Flat}
          size="sm"
          className="text-fg-muted hover:bg-bg-subtle"
          onClick={handleClickStudySnapshot}
          data-testid="snapshot-header-button"
          icon="icon-table-2"
          disabled={!snapshotEnabled}
          iconRight={
            isStudySnapshotOpened && snapshotEnabled
              ? "icon-chevron-up"
              : "icon-chevron-down"
          }
        />
      </Popover>
    );
  };
  // Only show source checkbox on the thread page (vs bookmark lists etc.)
  // usePaperSidebar is a proxy for this state.
  const showSourceCheckbox = usePaperSidebar && isDataSourceSelectorEnabled;
  const isLoadingFeaturedText =
    (isLoadingExtractedAnswer && !extractedAnswer) ||
    (shouldFetchTakeawayTranslation &&
      !!display_text &&
      isLoadingTakeawayTranslation);

  // Prepare shared props for all view components
  const sharedViewProps = {
    paper: item,
    index,
    text,
    textType,
    relativeLink,
    absoluteLink,
    isRetracted,
    isLoadingTitle,
    isProcessingMetadata,
    isLoadingExtractedAnswer,
    citation,
    meter,
    badges,
    extraTags,
    resultFormat,
    queryText,
    showCitationDashes,
    showPaperDoi,
    showSaveBtn,
    showCiteBtn,
    showShareBtn,
    showManageUploadedPaperBtn,
    showSourceCheckbox,
    recentlyUpdated,
    recentlyAdded,
    finalRanking,
    externalLink,
    openDetailsInNewTab,
    usePaperSidebar,
    hideCitations,
    hashPaperRecordId,
    isUploading,
    isManageDropdownOpen,
    onClickItem,
    onResultClick: handleResultClick,
    onClickSave,
    onClickCite,
    onCitePaper,
    onFullTextClick,
    onSharePaper,
    onManageDropdownOpenChange: handleManageDropdownOpenChange,
    mixpanelActionSource,
    interactionId,
    researchHubUploadingStateLabels: researchHubUploadingStateLabels,
    debugInfo: item.debug_info,
    // Default view specific props
    isLoadingFeaturedText: isLoadingFeaturedText,
    directQuotes,
    selectedQuoteIndex,
    setSelectedQuoteIndex,
    isStudySnapshotOpened,
    snapshotEnabled,
    snapshotButton: renderSnapshotButton(),
    sequence,
    lang,
  };

  if (resultFormat === ResultFormat.TABLE) {
    return <SearchResultTableRow {...sharedViewProps} />;
  }

  if (resultFormat === ResultFormat.COMPACT) {
    return <SearchResultCompact {...sharedViewProps} />;
  }

  return <SearchResultDefault {...sharedViewProps} />;
};

export default SearchResult;
